import { useEffect, useState } from 'react';
import { Users, ShoppingCart, Send, DollarSign, TrendingUp } from 'lucide-react';

interface MetaClick {
  id: string;
  fbclid: string;
  phone: string;
  user_name: string;
  user_email: string;
  status: string;
  created_at: string;
  events: {
    pageview: boolean;
    addtocart: boolean;
    initiatecheckout: boolean;
    purchase: boolean;
  };
}

interface FunnelStats {
  total_clicks: number;
  pageviews: number;
  addtocarts: number;
  checkouts: number;
  purchases: number;
  conversion_rate: number;
}

export default function MetaFunil() {
  const [clicks, setClicks] = useState<MetaClick[]>([]);
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: '',
    end_date: '',
    status: 'all'
  });

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);
      if (filter.status !== 'all') params.append('status', filter.status);

      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/meta/funnel?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (data.success) {
        setClicks(data.data.clicks || []);
        setStats(data.data.stats || null);
      }
    } catch (error) {
      console.error('Erro ao carregar funil:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando funil de conversão...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Funil de Conversão - Meta Pixel</h1>
        <p className="text-gray-600">Acompanhe a jornada dos clientes desde o clique no anúncio até a compra</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({...filter, start_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({...filter, end_date: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({...filter, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">Todos</option>
              <option value="pending">Pendente</option>
              <option value="linked">Vinculado</option>
              <option value="converted">Convertido</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cliques</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_clicks}</p>
              </div>
              <Users className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Visitaram</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pageviews}</p>
                <p className="text-xs text-gray-500">{((stats.pageviews / stats.total_clicks) * 100).toFixed(1)}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-indigo-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Adicionaram</p>
                <p className="text-2xl font-bold text-gray-900">{stats.addtocarts}</p>
                <p className="text-xs text-gray-500">{((stats.addtocarts / stats.pageviews) * 100).toFixed(1)}%</p>
              </div>
              <ShoppingCart className="h-10 w-10 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Enviaram</p>
                <p className="text-2xl font-bold text-gray-900">{stats.checkouts}</p>
                <p className="text-xs text-gray-500">{((stats.checkouts / stats.addtocarts) * 100).toFixed(1)}%</p>
              </div>
              <Send className="h-10 w-10 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Compraram</p>
                <p className="text-2xl font-bold text-green-600">{stats.purchases}</p>
                <p className="text-xs text-gray-500">{stats.conversion_rate.toFixed(1)}% total</p>
              </div>
              <DollarSign className="h-10 w-10 text-green-500" />
            </div>
          </div>
        </div>
      )}

      {/* Tabela de Clientes */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Clientes no Funil</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jornada</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {clicks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    Nenhum cliente encontrado
                  </td>
                </tr>
              ) : (
                clicks.map((click) => (
                  <tr key={click.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{click.user_name || 'Não identificado'}</div>
                      <div className="text-sm text-gray-500">{click.user_email || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {click.phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        click.status === 'converted' ? 'bg-green-100 text-green-800' :
                        click.status === 'linked' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {click.status === 'converted' ? 'Convertido' :
                         click.status === 'linked' ? 'Vinculado' : 'Pendente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          click.events.pageview ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'
                        }`} title="PageView">
                          👁️
                        </span>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          click.events.addtocart ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-400'
                        }`} title="AddToCart">
                          🛒
                        </span>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          click.events.initiatecheckout ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-400'
                        }`} title="InitiateCheckout">
                          📤
                        </span>
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          click.events.purchase ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                        }`} title="Purchase">
                          💰
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(click.created_at).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
